/**
 * Peças do Guia de conexão: cada uma faz UMA coisa da pessoa, usando os mesmos
 * endpoints de "Calendário no celular" e "Integrações de IA" (nada novo no
 * servidor). Os textos vêm de `passos.js`.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";

import AsyncButton from "../../../components/AsyncButton";
import BotaoCopiar from "../../../components/BotaoCopiar";
import { gerarSenhaCalDAV, iniciarGoogle } from "../../../services/agenda";
import { Credencial } from "../tabs/SincronizacaoCalendarios";
import { PASSOS_GOOGLE, ehComando } from "./passos";

export const TELA_CALENDARIO = "/configuracoes/calendario-celular";
export const TELA_IA = "/configuracoes/integracoes-ia";

const formato = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
});
const instante = (iso) => (iso ? formato.format(new Date(iso)) : "");

/** Um passo numerado. `feito` mostra o check verde no lugar do número. */
export function Passo({ numero, titulo, feito = false, children }) {
  return (
    <li className={`gc-passo${feito ? " feito" : ""}`}>
      <span className="gc-num" aria-hidden="true">{feito ? "✓" : numero}</span>
      <div className="gc-passo-c">
        <b>{titulo}</b>
        {children}
      </div>
    </li>
  );
}

/** Abas de aparelhos ou de IAs; troca sem recarregar nada. */
export function Abas({ grupos, rotulo, valor, onTrocar }) {
  return (
    <div className="ia-abas gc-abas" role="tablist" aria-label={rotulo}>
      {grupos.map((g) => (
        <button key={g.id} type="button" role="tab" aria-selected={g.id === valor} className={g.id === valor ? "ativa" : ""} onClick={() => onTrocar(g.id)}>
          {g.nome}{g.novo ? <span className="gc-novo">Novo</span> : null}
        </button>
      ))}
    </div>
  );
}

/** Lista de frases do passo a passo, com `{url}`/`{servidor}` já trocados. */
export function Frases({ passos, trocas = {} }) {
  const trocar = (texto) => Object.entries(trocas).reduce((t, [chave, valor]) => t.replaceAll(`{${chave}}`, valor), texto);
  return (
    <ol className="gc-frases">
      {passos.map((passo) => (ehComando(passo) ? (
        <li key={passo} className="ia-comando"><code>{trocar(passo)}</code><BotaoCopiar texto={trocar(passo)} aria-label="Copiar o comando" /></li>
      ) : <li key={passo}>{trocar(passo)}</li>))}
    </ol>
  );
}

export function BlocoGoogle({ request, google, numero = 1 }) {
  if (!google?.configurado) {
    return <Passo numero={numero} titulo="Google Agenda ainda não disponível"><p>O Google Agenda ainda não foi configurado neste servidor.</p></Passo>;
  }
  if (google.conectado && !google.precisa_reconectar) {
    return (
      <Passo numero={numero} titulo="Google Agenda conectado" feito>
        <p>{`Conectado como ${google.conta || "a sua conta Google"}. Procure o calendário “Pharu ERP” no Google Agenda.`}</p>
      </Passo>
    );
  }
  const conectar = async () => {
    const { url } = await iniciarGoogle(request);
    window.location.assign(url);
  };
  return (
    <Passo numero={numero} titulo={google.precisa_reconectar ? "Conecte de novo o Google Agenda" : "Conecte o Google Agenda"}>
      <Frases passos={PASSOS_GOOGLE} />
      <AsyncButton variant="primary" onClick={conectar} silentSuccess silentError>
        {google.precisa_reconectar ? "Conectar de novo" : "Conectar o Google Agenda"}
      </AsyncButton>
    </Passo>
  );
}

/** Senha de app do CalDAV: gera aqui mesmo e mostra uma vez. */
export function BlocoSenha({ request, caldav, onMudou, numero = 1 }) {
  const [senha, setSenha] = useState("");
  const gerar = async () => {
    const resposta = await gerarSenhaCalDAV(request);
    setSenha(resposta.caldav.senha);
    onMudou(resposta);
  };
  if (senha) {
    return (
      <Passo numero={numero} titulo="Anote estes três dados: a senha só aparece agora" feito>
        <div className="gc-dados">
          <Credencial rotulo="Servidor" valor={caldav.servidor} />
          <Credencial rotulo="Nome de usuário" valor={caldav.usuario} />
          <Credencial rotulo="Senha" valor={senha} />
        </div>
      </Passo>
    );
  }
  if (caldav?.ativa) {
    return (
      <Passo numero={numero} titulo="Você já tem uma senha do calendário" feito>
        <div className="gc-dados">
          <Credencial rotulo="Servidor" valor={caldav.servidor} />
          <Credencial rotulo="Nome de usuário" valor={caldav.usuario} />
        </div>
        <p>
          Senha: a que apareceu quando você gerou. Não lembra? Gere outra em{" "}
          <Link to={TELA_CALENDARIO}>Calendário no celular</Link> (a antiga para de funcionar).
        </p>
      </Passo>
    );
  }
  return (
    <Passo numero={numero} titulo="Gere a sua senha do calendário">
      <p>É uma senha só para o calendário, diferente da senha do ERP. Ela aparece uma vez: deixe o celular por perto.</p>
      <AsyncButton variant="primary" onClick={gerar} silentSuccess silentError>Gerar minha senha</AsyncButton>
    </Passo>
  );
}

/** "Ver se deu certo": lê de novo o estado e diz o que chegou. */
export function Conferir({ numero, onConferir, resultado }) {
  return (
    <Passo numero={numero} titulo="Veja se deu certo" feito={resultado?.ok}>
      <AsyncButton variant="secondary" onClick={onConferir} silentSuccess silentError>Conferir agora</AsyncButton>
      {resultado ? <p className={resultado.ok ? "gc-ok" : "gc-ainda"} role="status">{resultado.texto}</p> : null}
    </Passo>
  );
}

export function resultadoDoCalDAV(caldav) {
  if (caldav?.ultimo_uso_em) return { ok: true, texto: `Deu certo: o seu aparelho falou com o ERP em ${instante(caldav.ultimo_uso_em)}.` };
  return { ok: false, texto: "Ainda não chegou nenhum aparelho. Confira se o servidor, o usuário e a senha estão iguais aos daqui." };
}

export function resultadoDoGoogle(google) {
  if (google?.conectado && !google.precisa_reconectar) return { ok: true, texto: `Deu certo: conectado como ${google.conta || "a sua conta Google"}.` };
  return { ok: false, texto: "O Google ainda não está conectado. Clique em Conectar o Google Agenda e siga a tela do Google." };
}

/** IAs que entraram pelo login do ERP, mais as chaves pessoais já usadas (Claude Code). */
export function resultadoDaIa(integracoes) {
  const nomes = [...new Set((integracoes?.conexoes || []).map((c) => c.aplicativo).filter(Boolean))];
  const chaves = (integracoes?.chaves || []).filter((k) => k.ultimo_uso_em && !k.vencida).map((k) => k.nome);
  const conectadas = [...nomes, ...chaves.map((nome) => `chave pessoal “${nome}”`)];
  if (conectadas.length) {
    return { ok: true, texto: `Deu certo! Conectado: ${conectadas.join(", ")}. Peça à IA algo como “o que tenho hoje na agenda?”.` };
  }
  return { ok: false, texto: "Nenhuma IA conectada ainda. Termine os passos na sua IA (no fim, clique em Permitir na página do ERP)." };
}
